import React, { useEffect, useState,useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { Editor } from "@tinymce/tinymce-react";
import { useParams, Link } from "react-router-dom";
import { Wallet, Banknote } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import iiucLogo from "/img/logo.png"; 

// ApplyJob.jsx (updated to consume provided Job Details API response)
// - Expects /job-info POST to return an array [ { ...job fields... } ] or a single object
// - Uses application_fee as amount (read-only)
// - Renders qualification and additional_requirement as HTML (safe-ish, server-controlled)

export default function ApplyJob() {
	const { jobId } = useParams(); 
	const [job, setJob] = useState(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const editorRef = useRef(null);
  // Form state
  const [paymentMethod, setPaymentMethod] = useState("");
  const [amount, setAmount] = useState(""); // read-only populated from job.application_fee
  const [transactionDetails, setTransactionDetails] = useState("");
  const [coverLetter, setCoverLetter] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [subject, setSubject] = useState("");

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = {
          userId: import.meta.env.VITE_API_USER,
          password: import.meta.env.VITE_API_PASS,          
          id: jobId,
        };
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});
		const res = await api.post("/career-details", payload);
        
        const data = res.data;
        // API returns array with single object per your sample
        const jobObj = Array.isArray(data) ? (data[0] || {}) : data || {};
        setJob(jobObj);

        const amt = jobObj.application_fee ?? jobObj.amount ?? jobObj.fee ?? 0;
        setAmount(String(amt));
      } catch (err) {
        console.error(err);
        setError("Failed to load job information.");
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobId]);

  const V = {
    container: { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } },
    fade: { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.36 } } },
  };

  const validate = () => {
    const errs = {};
    if (!paymentMethod) errs.paymentMethod = "Select payment method";
    if (!amount) errs.amount = "Amount not available";
    if (!transactionDetails) errs.transactionDetails = "Provide transaction details (Transaction ID)";
    if (!coverLetter || coverLetter.replace(/<[^>]*>/g, "").trim().length < 20) errs.coverLetter = "Cover letter must be at least 20 characters";

    if (paymentMethod === "bKash") {
      if (!/^\w{3,}$/i.test(transactionDetails)) errs.transactionDetails = "Enter a valid bKash Transaction ID";
    }

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setError(null);

    try {
		const payload = {
			userId: import.meta.env.VITE_API_USER,
			password: import.meta.env.VITE_API_PASS,
			user: secureLocalStorage.getItem("user"),
			token:secureLocalStorage.getItem("token"),
			id: jobId,
			paymentMethod,
			amount,
			transactionDetails,
			coverLetter,
			subject,
		};
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});	
		const res = await api.post("/apply-job", payload);
		const code = res?.data?.responseCode?.toString();
		if (code === "200") {
			// replace alert with nicer UI as needed
			alert(res.data.responseMsg || "Application submitted successfully.");
			setPaymentMethod("");
			setTransactionDetails("");
			setCoverLetter("");
		} else {
			throw new Error(res.data?.responseMsg || "Submission failed");
		}
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || "Failed to submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6">Loading job info...</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!job) return <div className="p-6">No job information available.</div>;

  return (
    <motion.form initial="hidden" animate="visible" variants={V.container} onSubmit={handleSubmit} className="max-w-6xl mx-auto p-6">
      {/* Header SVG + Job Summary */}
      <div className="relative overflow-hidden rounded-2xl mb-6 bg-gradient-to-r from-[#1A2B63] via-[#0E1B40] to-[#0E1B40]">
        
        <div className="relative z-10 p-6">
          <motion.h2 variants={V.fade} className="text-xl md:text-2xl font-bold text-white">{job.designation_name || job.title || 'Job Position'}</motion.h2>
          <motion.div variants={V.fade} className="text-sm text-white/90 mt-2 space-y-2">
            <div><strong>Department:</strong> {job.department_name}</div>
            <div><strong>Subject:</strong> {job.subject}</div>
            <div><strong>Position(s):</strong> {job.position_number}</div>
            <div><strong>Job type:</strong> {job.job_type}</div>
            <div><strong>Open:</strong> {job.open_date} • <strong>Deadline:</strong> {job.deadline}</div>
            <div><strong>Application Fee:</strong> {job.application_fee || '0'}</div>
          </motion.div>
        </div>
      </div>
	  <motion.div
		className="mx-auto mt-10 p-3"
		initial={{ opacity: 0, y: 20 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.8 }}
	   >
      <div className="flex items-center justify-center mb-6">
        <img src={iiucLogo} alt="IIUC Logo" className="w-14 h-14 mr-3" />
        <h2 className="text-2xl md:text-3xl font-bold text-[#1A2B63]">
          Payment Instruction
        </h2>
      </div>

      {/* bKash Payment Option */}
      <Card className="mb-5 border border-[#1A2B63]/20 shadow-md hover:shadow-lg transition-all duration-300 rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-center mb-3">
            <Wallet className="text-pink-600 w-7 h-7 mr-2" />
            <h3 className="text-xl font-semibold text-[#1A2B63]">
              Option 1 – Pay via bKash
            </h3>
          </div>

          <p className="text-gray-700 leading-relaxed mb-3">
            Pay <strong>Tk. {amount}</strong> as the application fee to the{" "}
            <strong>bKash Merchant Number: 01973209030</strong>.
          </p>

          <ol className="list-decimal list-inside text-gray-700 space-y-1 ml-3">
            <li>Dial <code className="bg-gray-100 px-2 py-0.5 rounded">*247#</code> on your mobile.</li>
            <li>Select <strong>Payment</strong> option.</li>
            <li>Enter Merchant Number: <strong>01973209030</strong></li>
            <li>Enter Amount: <strong>{amount}</strong></li>
            <li>Enter your <strong>short name</strong> as Reference.</li>
            <li>Enter Counter No.: <strong>1</strong></li>
            <li>Enter your <strong>PIN</strong> to confirm.</li>
          </ol>
        </CardContent>
      </Card>

      {/* Cash Payment Option */}
      <Card className="border border-[#1A2B63]/20 shadow-md hover:shadow-lg transition-all duration-300 rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-center mb-3">
            <Banknote className="text-green-600 w-7 h-7 mr-2" />
            <h3 className="text-xl font-semibold text-[#1A2B63]">
              Option 2 – Pay in Bank Draft 
            </h3>
          </div>
          <p className="text-gray-700 leading-relaxed">
            You can also deposit the application fee in <strong>Bank Draft</strong> at the{" "}
            <strong>IBBL Chawkbazar Branch, IIUC, Kumira, Chattogram.</strong>
          </p>
        </CardContent>
      </Card>
    </motion.div>
      {/* Row 2: Payment inputs */}
      <motion.div variants={V.fade} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method *</label>
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full border rounded px-3 py-2">
            <option value="">Select method</option>
            <option value="bKash">bKash</option>            
            <option value="Bank">Bank</option>
          </select>
          {formErrors.paymentMethod && <div className="text-red-600 text-xs mt-1">{formErrors.paymentMethod}</div>}
		</div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
          <input readOnly value={amount} className="w-full border rounded px-3 py-2 bg-slate-50" />
          {formErrors.amount && <div className="text-red-600 text-xs mt-1">{formErrors.amount}</div>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Transaction Details *</label>
          <input value={transactionDetails} onChange={(e) => setTransactionDetails(e.target.value)} placeholder={paymentMethod === 'bKash' ? 'bKash Transaction ID (e.g. "TX12345")' : 'Transaction details or receipt number'} className="w-full border rounded px-3 py-2" />
          {paymentMethod === 'bKash' && <div className="text-xs text-slate-500 mt-1">For bKash please enter the bKash Transaction ID only.</div>}
          {formErrors.transactionDetails && <div className="text-red-600 text-xs mt-1">{formErrors.transactionDetails}</div>}
        </div>
      </motion.div>

      <motion.div variants={V.fade} className="mb-4">
		  <label className="block text-sm font-medium text-slate-700 mb-2">
			Subject on Expertise *
		  </label>

		  <input
			type="text"
			value={subject}
			onChange={(e) => setSubject(e.target.value)}
			placeholder="Enter application subject"
			className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
		  />

		  {formErrors.subject && (
			<div className="text-red-600 text-xs mt-1">
			  {formErrors.subject}
			</div>
		  )}
		</motion.div>

      {/* Row 4: Cover Letter (TinyMCE) */}
      <motion.div variants={V.fade} className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">Cover Letter *</label>
        <Editor
          ref={editorRef}
		  tinymceScriptSrc="/tinymce/tinymce.min.js"
		  licenseKey="gpl"
          init={{
            height: 300,
            menubar: false,
            plugins: ["advlist autolink lists link charmap preview anchor", "searchreplace visualblocks code", "insertdatetime table paste help wordcount"],
            toolbar: "undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
          }}
          value={coverLetter}
          onEditorChange={(content) => setCoverLetter(content)}
        />
        {formErrors.coverLetter && <div className="text-red-600 text-xs mt-1">{formErrors.coverLetter}</div>}
      </motion.div>

      {/* Submit */}
      <motion.div variants={V.fade} className="flex items-center justify-end gap-3">
        <button type="button" onClick={() => { setPaymentMethod(''); setTransactionDetails(''); setCoverLetter(''); }} className="text-sm text-slate-500">Cancel</button>

        <button disabled={submitting} type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded shadow-sm hover:opacity-95">{submitting ? 'Submitting...' : 'Submit Application'}</button>
      </motion.div>
    </motion.form>
  );
}
